/**
 * Application root.
 *
 * The provider order is load-bearing and is stated here so it is not rearranged by
 * accident:
 *
 * 1. `ThemeProvider` - outermost. It writes a class to `<html>` and nothing depends
 *    on it, so mounting it anywhere else would only mean its effect ran after every
 *    other provider's effects for no reason.
 * 2. `AuthProvider` - owns the API client and the access token. Everything below it
 *    that talks to the server goes through the client it exposes, which is what
 *    keeps the 401-retry logic in one place.
 * 3. `Root` - decides between the splash, the sign-in form and the shell.
 *
 * `BoardProvider` is deliberately *not* here. It mounts inside the shell, keyed on
 * the board id, so that changing boards is an unmount and a fresh mount - the
 * socket closes and a new one opens, and there is no window in which one board's
 * frames are applied to another's state.
 *
 * ## The splash
 *
 * `status === 'checking'` means the initial refresh probe has not resolved. The
 * alternative to a splash is rendering the sign-in form and replacing it a moment
 * later, which is a visible flash of "you are logged out" for every signed-in user
 * who reloads. The splash is deliberately minimal and has no spinner animation
 * delay, because it resolves in one round trip.
 */

import type { ReactNode } from 'react';
import { AuthProvider, useAuth } from '../state/AuthProvider.js';
import { ThemeProvider } from '../state/ThemeProvider.js';
import { SignIn } from './SignIn.js';
import { WorkspaceShell } from './WorkspaceShell.js';
import { Spinner } from './ui.js';

export function App(): ReactNode {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Root />
      </AuthProvider>
    </ThemeProvider>
  );
}

function Root(): ReactNode {
  const { status } = useAuth();

  if (status === 'checking') {
    return (
      <div className="flex h-full items-center justify-center bg-canvas">
        <Spinner label="Checking your session" size={20} />
      </div>
    );
  }

  return status === 'signed-in' ? <WorkspaceShell /> : <SignIn />;
}
